import { useState } from "react";
import type { Identifier } from "ra-core";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { CHANNELS, OUTCOMES, type Channel, type Outcome } from "./contactLinks";
import { useRefreshTimeline } from "./useRefreshTimeline";

// "Log contact" (task 1.9, D-038): one tap opens the panel, one tap on an
// outcome saves it, with the optional note typed before. tenax_add_note
// accepts a note without text when it has a channel and an outcome, the
// same rule for the app, the Claude connector and the agents.
export const QuickContactLog = ({
  target,
  targetId,
}: {
  target: "contact" | "company";
  targetId: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refreshTimeline = useRefreshTimeline();
  const [isOpen, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [isSaving, setSaving] = useState(false);

  const save = async (channel: Channel, outcome: Outcome) => {
    setSaving(true);
    const { error } = await getSupabaseClient().rpc("tenax_add_note", {
      p_target: target,
      p_target_id: Number(targetId),
      p_text: note,
      p_channel: channel,
      p_outcome: outcome,
    });
    setSaving(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    if (target === "contact") {
      // as Atomic does when a note is added from the contact page
      await dataProvider.update("contacts", {
        id: targetId,
        data: { last_seen: new Date().toISOString() },
        previousData: { id: targetId },
      });
    }
    setOpen(false);
    setNote("");
    notify("tenax.timeline.logged", { type: "info" });
    await refreshTimeline();
  };

  const channelLabel = (channel: Channel) =>
    translate(`tenax.people.channel.${channel}`);
  const outcomeLabel = (outcome: Outcome) =>
    translate(`tenax.timeline.outcome.${outcome}`);

  return (
    <>
      <Button type="button" onClick={() => setOpen(true)}>
        {translate("tenax.timeline.log")}
      </Button>
      <Sheet open={isOpen} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[90dvh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{translate("tenax.timeline.log_title")}</SheetTitle>
            <SheetDescription>
              {translate("tenax.timeline.log_hint")}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-6">
            <div className="flex flex-col gap-1">
              <Label htmlFor="tenax-quick-note">
                {translate("tenax.timeline.note")}
              </Label>
              <Textarea
                id="tenax-quick-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            {CHANNELS.map((channel) => (
              <section
                key={channel}
                aria-label={channelLabel(channel)}
                className="flex flex-col gap-1"
              >
                <h3 className="text-sm font-semibold">
                  {channelLabel(channel)}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {OUTCOMES.map((outcome) => (
                    <Button
                      key={outcome}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10"
                      disabled={isSaving}
                      onClick={() => save(channel, outcome)}
                    >
                      {outcomeLabel(outcome)}
                    </Button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
